"use client";

import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOnce, usePubSub } from "@huub-web/ui";

import { useTemplates } from "./useTemplates";

type UseOcktoQRParams = {
  /**
   * API endpoint that returns the QR code as a base64 string
   */
  endpoint: string;
};

type UseOcktoQRResult = {
  /**
   * Base64 data URL for the QR code image
   */
  src: string | undefined;
  /**
   * Whether the QR code is currently being fetched
   */
  loading: boolean;
  /**
   * Fetch or refresh the QR code
   */
  handleRefresh: () => void;
};

/**
 * Hook that manages Ockto QR code fetching, base64 conversion,
 * loading state, and error handling.
 *
 * The lockUI guard is handled by consumers (disabling the refresh button),
 * not inside the hook — this ensures the initial fetch on mount always fires.
 * Triggers initial fetch on mount via `useOnce`.
 */
export const useOcktoQR = ({
  endpoint,
}: UseOcktoQRParams): UseOcktoQRResult => {
  const { t } = useTranslation();
  const { $get } = useTemplates();
  const { publish } = usePubSub();

  const [src, setSrc] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);

  const handleRefresh = useCallback(async () => {
    if (isLoadingRef.current || !endpoint) {
      return;
    }

    setIsLoading(true);
    isLoadingRef.current = true;

    try {
      const response = await $get(endpoint);
      const base64String = response?.data?.data?.content;

      if (base64String && typeof base64String === "string") {
        setSrc(`data:image/png;base64,${base64String}`);
      } else {
        setSrc(undefined);
      }
    } catch (error) {
      console.error("Error fetching QR code:", error);
      setSrc(undefined);
      publish("snackbar", {
        variant: "error",
        title: t("ocktoCard.qrErrorTitle"),
        body: t("ocktoCard.qrErrorBody"),
        autoHide: 5,
        closeable: true,
      });
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [endpoint, t, publish, $get]);

  useOnce(() => {
    handleRefresh();
  });

  return { src, loading: isLoading, handleRefresh };
};
